import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ethers } from 'ethers';
import { provider, signer, ethersUtils, createContract, getAccounts, connectionError, initializeEthers, walletManager } from './EthersSetup.js';
import RockPaperScissorsConfig from '../deployedContractsInfo/RockPaperScissors.json';
import GameMakerConfig from '../deployedContractsInfo/GameMaker.json';
import contractAddresses from '../deployedContractsInfo/contract-address.json';
import blockchainDataManager from '../services/BlockchainDataManager.js';
import AddressDisplay, { AddressDisplayLong } from './AddressDisplay.js';
import { TransactionLink } from './LinkDisplay';
import TopRightBar from './basics/TopRightBar';
import GameTransactionHistory from './basics/GameTransactionHistory';
import { FullGameSkeleton, TransactionPanelSkeleton } from './ui/SkeletonLoader';

import RockIcon from '../pictures/rock.png';
import PaperIcon from '../pictures/paper.png';
import ScissorsIcon from '../pictures/scissors.png';

const RockPaperScissorsGame = () => {
  const { gameID = '1' } = useParams();
  const gameIDNumber = parseInt(gameID); // Convert to number for contract calls
  const [gameState, setGameState] = useState({
    player1: '',
    player2: '',
    gameFinished: false,
    revealedChoice1: null,
    revealedChoice2: null,
    hashedChoice1: null,
    hashedChoice2: null,
    your_hashedChoice: null,
    your_revealedChoice: null,
    opp_hashedChoice: null,
    opp_revealedChoice: null,
    flag_makeYourChoice: null,
    flag_waitOpponentChoice: null,
    flag_revealYourChoice: null,
    flag_waitOpponentReveal: null,
    flag_gameFinished: null,
    your_address: null,
    opp_address: null,
    statusString: '',
    contribution1: 0,
    contribution2: 0,
    lastMoveTime: 0,
    winner: ''
  });

  const [userAddress, setUserAddress] = useState();
  const [isWalletInitialized, setIsWalletInitialized] = useState(false);
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [selectedRevealChoice, setSelectedRevealChoice] = useState(null);
  const [originalChoice, setOriginalChoice] = useState(localStorage.getItem(`originalChoice_RockPaperScissors_${gameID}`) || null);
  const [salt, setSalt] = useState(localStorage.getItem(`salt_RockPaperScissors_${gameID}`) || '');
  const [errorMessage, setErrorMessage] = useState('');
  const [uiReady, setUiReady] = useState(false);
  const [loadingStages, setLoadingStages] = useState({
    gameBasics: true,    // Player names, status, game phase
    gameDetails: true,   // Choices and reveals
    transactions: true,  // Transaction history
    userFeatures: true   // Wallet-dependent features
  });
  const [rulesCollapsed, setRulesCollapsed] = useState(true);
  const [transactionPending, setTransactionPending] = useState(null); // { type: 'commit'|'reveal', choice: string, message: string }
  const transactionPendingRef = useRef(null);

  // Memoized game events for GameTransactionHistory to prevent constant reloading
  const rpsGameEvents = useMemo(() => [
    { name: 'ChoiceMade', label: 'Choice Made' },
    { name: 'ChoiceRevealed', label: 'Choice Revealed' }
  ], []);
  
  // Helper function to set transaction pending state and ref
  const setTransactionPendingState = useCallback((pendingState) => {
    setTransactionPending(pendingState);
    transactionPendingRef.current = pendingState;
  }, []);

  // Contract state management for async creation
  const [readOnlyContract, setReadOnlyContract] = useState(null);
  const [gameMakerContract, setGameMakerContract] = useState(null);

  // Initialize read-only contract asynchronously
  useEffect(() => {
    const initReadOnlyContract = async () => {
      try {
        const contract = await createContract(contractAddresses.RockPaperScissors, RockPaperScissorsConfig.abi, false);
        setReadOnlyContract(contract);
      } catch (error) {
        console.warn('[RockPaperScissors] Failed to create read-only contract:', error);
        setReadOnlyContract(null);
      }
    };
    initReadOnlyContract();
  }, []);

  // Create GameMaker contract for transaction history
  useEffect(() => {
    const initGameMakerContract = async () => {
      try {
        const contract = await createContract(contractAddresses.GameMaker, GameMakerConfig.abi);
        setGameMakerContract(contract);
      } catch (error) {
        console.error('RockPaperScissors: Failed to create GameMaker contract:', error);
      }
    };
    initGameMakerContract();
  }, []);

  const [rockPaperScissorsContract, setRockPaperScissorsContract] = useState(null);
  const [signerContract, setSignerContract] = useState(null);

  // Create Ethers.js contract instance for event listening
  const [ethersContract, setEthersContract] = useState(null);

  // Initialize wallet from existing connection
  useEffect(() => {
    // Prevent multiple initializations
    if (isWalletInitialized) {
      console.log('💫 RockPaperScissors: Wallet already initialized, skipping');
      return;
    }

    console.log('💫 RockPaperScissors: Starting wallet state sync');
    const syncWalletState = async () => {
      try {
        console.log('💫 RockPaperScissors: Checking existing wallet state');

        // Check if we already have a provider and signer (from wallet manager)
        console.log('💫 RockPaperScissors: Checking wallet state - provider:', !!provider, 'walletManager connected:', walletManager?.isConnected());

        if (provider) {
          console.log('💫 RockPaperScissors: Provider found, getting accounts...');
          try {
            const accounts = await getAccounts();
            console.log('💫 RockPaperScissors: getAccounts() result:', accounts, 'length:', accounts.length);
            if (accounts.length > 0) {
              setUserAddress(accounts[0]);
              console.log('💫 RockPaperScissors: Using existing wallet address:', accounts[0]);
            } else {
              console.log('💫 RockPaperScissors: No accounts found from getAccounts(), trying to initialize wallet');
              await initializeEthers();
              const newAccounts = await getAccounts();
              console.log('💫 RockPaperScissors: After initializeEthers(), getAccounts() result:', newAccounts, 'length:', newAccounts.length);
              if (newAccounts.length > 0) {
                setUserAddress(newAccounts[0]);
                console.log('💫 RockPaperScissors: Wallet initialized with address:', newAccounts[0]);
              } else {
                console.log('💫 RockPaperScissors: Still no accounts after initialization');
              }
            }
          } catch (error) {
            console.error('💫 RockPaperScissors: Error during getAccounts():', error);
          }
        } else {
          console.log('💫 RockPaperScissors: No provider, initializing wallet');
          try {
            await initializeEthers();
            const accounts = await getAccounts();
            console.log('💫 RockPaperScissors: After initializeEthers() (no provider), getAccounts() result:', accounts, 'length:', accounts.length);
            if (accounts.length > 0) {
              setUserAddress(accounts[0]);
              console.log('💫 RockPaperScissors: Wallet initialized with address:', accounts[0]);
            } else {
              console.log('💫 RockPaperScissors: No accounts after wallet initialization');
            }
          } catch (error) {
            console.error('💫 RockPaperScissors: Error during wallet initialization:', error);
          }
        }
        // Add delay to allow wallet state to propagate after wallet switching
        await new Promise(resolve => setTimeout(resolve, 1000));
        setIsWalletInitialized(true);
        console.log('💫 RockPaperScissors: Wallet state sync complete');
      } catch (error) {
        console.error('RockPaperScissors: Failed to sync wallet state:', error);
        setIsWalletInitialized(true); // Still allow component to continue
      }
    };
    syncWalletState();
  }, [isWalletInitialized]); // Add isWalletInitialized to dependencies

  // Fallback: Set wallet as initialized if we have a user address
  useEffect(() => {
    if (userAddress && !isWalletInitialized) {
      console.log('💫 RockPaperScissors: Setting wallet initialized based on userAddress');
      setIsWalletInitialized(true);
    }
  }, [userAddress, isWalletInitialized]);

  // Set browser tab title for the game
  useEffect(() => {
    const originalTitle = document.title;
    document.title = `RockPaperScissors #${gameID}`;
    
    // Cleanup: restore original title when component unmounts
    return () => {
      document.title = originalTitle;
    };
  }, [gameID]);

  // Remove duplicate user data loading - now handled in wallet initialization

  // Initialize contract when wallet is available
  useEffect(() => {
    console.log('🔧 RockPaperScissors: Contract initialization check - isWalletInitialized:', isWalletInitialized, 'provider:', !!provider);
    if (!isWalletInitialized) {
      console.log('🔧 RockPaperScissors: Waiting for wallet initialization');
      return;
    }

    const initializeContract = async () => {
      try {
        console.log('🔧 RockPaperScissors: Creating contracts...');
        const contract = await createContract(contractAddresses.RockPaperScissors, RockPaperScissorsConfig.abi, true);
        setRockPaperScissorsContract(contract);
        console.log('🔧 RockPaperScissors: Main contract created and state updated');

        // Create ethers contract for events using the provider from EthersSetup
        if (provider) {
          const ethersContract = new ethers.Contract(contractAddresses.RockPaperScissors, RockPaperScissorsConfig.abi, provider);
          setEthersContract(ethersContract);
          console.log('🔧 RockPaperScissors: Ethers contract created');
        } else {
          console.log('🔧 RockPaperScissors: No provider available for ethers contract');
        }
      } catch (error) {
        console.error('Error creating RockPaperScissors contract:', error);
        setRockPaperScissorsContract(null);
        setEthersContract(null);
      }
    };

    initializeContract();
  }, [isWalletInitialized, createContract, provider]);

  // Monitor contract state changes
  useEffect(() => {
    console.log('📋 RockPaperScissors: Contract state changed:', {
      hasContract: !!rockPaperScissorsContract,
      hasEthersContract: !!ethersContract
    });
  }, [rockPaperScissorsContract, ethersContract]);

  // Monitor prerequisite state for debugging
  useEffect(() => {
    console.log('🎯 RockPaperScissors: Waiting for prerequisites:', {
      isWalletInitialized,
      hasUserAddress: !!userAddress,
      hasContract: !!rockPaperScissorsContract
    });
  }, [isWalletInitialized, userAddress, rockPaperScissorsContract]);

  // Listen to wallet manager events for real-time updates
  useEffect(() => {
    if (!walletManager) return;

    const handleAccountsChanged = async (accounts) => {
      console.log('🔄 RockPaperScissors: Wallet accounts changed:', accounts);
      if (accounts.length > 0) {
        setUserAddress(accounts[0]);
        console.log('🔄 RockPaperScissors: Updated user address:', accounts[0]);
      } else {
        setUserAddress(null);
        console.log('🔄 RockPaperScissors: No accounts found, cleared user address');
      }
    };

    const handleProviderChanged = ({ newProvider, walletType }) => {
      console.log(`🔄 RockPaperScissors: Wallet provider changed to: ${walletType}`);
      // Re-sync wallet state when provider changes
      const syncAfterProviderChange = async () => {
        const accounts = await getAccounts();
        if (accounts.length > 0) {
          setUserAddress(accounts[0]);
          console.log('🔄 RockPaperScissors: Synced address after provider change:', accounts[0]);
        }
      };
      syncAfterProviderChange();
    };

    walletManager.on('accountsChanged', handleAccountsChanged);
    walletManager.on('providerChanged', handleProviderChanged);

    return () => {
      walletManager.off('accountsChanged', handleAccountsChanged);
      walletManager.off('providerChanged', handleProviderChanged);
    };
  }, [walletManager]);

  const queryGameInstanceInfo = useCallback(async (isInitialLoad = false) => {
    console.log('🚀 queryGameInstanceInfo called with current state:', {
      isWalletInitialized,
      hasProvider: !!provider,
      hasContract: !!rockPaperScissorsContract,
      userAddress,
      isInitialLoad
    });
    try {
      if (!isWalletInitialized) {
        console.log('RockPaperScissors: Wallet not initialized yet');
        return;
      }
      if (connectionError) {
        console.error(connectionError);
        return;
      }
      if (!provider) {
        console.error('Web3 not initialized.');
        return;
      }
      if (!userAddress) {
        return;
      }
      if (!rockPaperScissorsContract) {
        console.log('RockPaperScissors contract not available yet. State check:', {
          isWalletInitialized,
          hasProvider: !!provider,
          hasContract: !!rockPaperScissorsContract,
          userAddress
        });
        return;
      }

      // Loading stages are managed by individual functions now

      // Execute all contract calls in parallel for better performance
      const [gameInstance, game, hashedChoices] = await Promise.all([
        rockPaperScissorsContract.getGameInstance(gameIDNumber),
        rockPaperScissorsContract.games(gameIDNumber),
        rockPaperScissorsContract.gamesHashedChoices(gameIDNumber)
      ]);

      const player1 = gameInstance.player1;
      const player2 = gameInstance.player2;
      const gameFinished = !gameInstance.gameActive;
      const lastMoveTime = Number(gameInstance.lastMoveTime);
      const contribution1 = game.contribution1;
      const contribution2 = game.contribution2;
      const hashedChoice1 = hashedChoices.hashedChoice1;
      const hashedChoice2 = hashedChoices.hashedChoice2;
      
      // Revealed choices are stored in the main game struct
      const revealedChoice1 = game.revealedChoice1;
      const revealedChoice2 = game.revealedChoice2;
      

      const your_address = userAddress;
      const opp_address = (player1.toLowerCase() === userAddress.toLowerCase()) ? player2 : player1;
      const your_hashedChoice = (player1.toLowerCase() === userAddress.toLowerCase()) ? hashedChoice1 : hashedChoice2;
      const your_revealedChoice = (player1.toLowerCase() === userAddress.toLowerCase()) ? revealedChoice1 : revealedChoice2;
      const opp_hashedChoice = (player1.toLowerCase() === userAddress.toLowerCase()) ? hashedChoice2 : hashedChoice1;
      const opp_revealedChoice = (player1.toLowerCase() === userAddress.toLowerCase()) ? revealedChoice2 : revealedChoice1;

      // Debug the variables used for flag calculation
      console.log('Flag calculation variables:', {
        userAddress,
        player1,
        player2,
        your_hashedChoice,
        opp_hashedChoice,
        your_revealedChoice: choiceFromUint(your_revealedChoice.toString()),
        opp_revealedChoice: choiceFromUint(opp_revealedChoice.toString()),
        revealedChoice1: choiceFromUint(revealedChoice1.toString()),
        revealedChoice2: choiceFromUint(revealedChoice2.toString()),
        hashedChoice1,
        hashedChoice2
      });

      const flag_makeYourChoice = (your_hashedChoice === "0x0000000000000000000000000000000000000000000000000000000000000000");
      const flag_waitOpponentChoice = (!flag_makeYourChoice) && (opp_hashedChoice === "0x0000000000000000000000000000000000000000000000000000000000000000");
      const flag_revealYourChoice = (!flag_makeYourChoice) && (opp_hashedChoice !== "0x0000000000000000000000000000000000000000000000000000000000000000") && (choiceFromUint(your_revealedChoice.toString()) === "Unknown");
      const flag_waitOpponentReveal = (!flag_makeYourChoice) && (opp_hashedChoice !== "0x0000000000000000000000000000000000000000000000000000000000000000") && (choiceFromUint(your_revealedChoice.toString()) !== "Unknown") && (choiceFromUint(opp_revealedChoice.toString()) === "Unknown");
      const flag_gameFinished = (choiceFromUint(revealedChoice1.toString()) !== "Unknown") && (choiceFromUint(revealedChoice2.toString()) !== "Unknown");

      let statusString = '';
      let winner = '';
      if (flag_gameFinished) {
        // Determine winner based on revealed choices
        const choice1 = Number(revealedChoice1);
        const choice2 = Number(revealedChoice2);
        
        // Only determine winner if both choices are valid
        if (choiceFromUint(revealedChoice1.toString()) !== "Unknown" && choiceFromUint(revealedChoice2.toString()) !== "Unknown") {
          if (choice1 === choice2) {
            statusString = 'Game ended in a draw';
            winner = 'draw';
          } else if (
            (choice1 === 1 && choice2 === 3) || // Rock beats Scissors
            (choice1 === 2 && choice2 === 1) || // Paper beats Rock
            (choice1 === 3 && choice2 === 2)    // Scissors beats Paper
          ) {
            statusString = 'Player 1 wins!';
            winner = player1;
          } else {
            statusString = 'Player 2 wins!';
            winner = player2;
          }
        }
      }

      const gameState_combined = {
        player1,
        player2,
        gameFinished,
        revealedChoice1: choiceFromUint(revealedChoice1.toString()),
        revealedChoice2: choiceFromUint(revealedChoice2.toString()),
        hashedChoice1,
        hashedChoice2,
        your_hashedChoice,
        your_revealedChoice: choiceFromUint(your_revealedChoice.toString()),
        opp_hashedChoice,
        opp_revealedChoice: choiceFromUint(opp_revealedChoice.toString()),
        flag_makeYourChoice,
        flag_waitOpponentChoice,
        flag_revealYourChoice,
        flag_waitOpponentReveal,
        flag_gameFinished,
        your_address,
        opp_address,
        statusString,
        contribution1,
        contribution2,
        lastMoveTime,
        winner
      };

      setGameState(gameState_combined);
      
      // Transaction information loaded by GameTransactionHistory component
      // Loading stages are managed by individual functions now
    } catch (error) {
      console.error('Failed to load game data:', error);
      setErrorMessage('Failed to load game data');
      // Loading stages are managed by individual functions now
    }
  }, [isWalletInitialized, userAddress, gameID, rockPaperScissorsContract]);

  // Removed loadTransactionInfo function - using GameTransactionHistory component instead

  const loadGameBasics = useCallback(async () => {
    try {
      const contractToUse = readOnlyContract || await createContract(contractAddresses.RockPaperScissors, RockPaperScissorsConfig.abi, false);
      if (!contractToUse) {
        throw new Error('No contract available');
      }

      const game = await contractToUse.games(gameIDNumber);
      const basicGameState = {
        player1: game.player1,
        player2: game.player2,
        gameFinished: game.gameFinished,
        contribution1: game.contribution1,
        contribution2: game.contribution2,
        lastMoveTime: Number(game.lastMoveTime)
      };

      setGameState(prev => ({ ...prev, ...basicGameState }));
      setLoadingStages(prev => ({ ...prev, gameBasics: false }));
      console.log('[RockPaperScissors] Game basics loaded');
    } catch (error) {
      console.error('[RockPaperScissors] Failed to load game basics:', error);
      setErrorMessage('Failed to load game information');
      setLoadingStages(prev => ({ ...prev, gameBasics: false }));
    }
  }, [gameIDNumber, readOnlyContract]);

  const loadGameDetails = useCallback(async () => {
    try {
      const contractToUse = readOnlyContract || await createContract(contractAddresses.RockPaperScissors, RockPaperScissorsConfig.abi, false);
      if (!contractToUse) {
        throw new Error('No contract available');
      }

      // Load raw game data and hashed choices
      const game = await contractToUse.games(gameIDNumber);
      const hashedChoices = await contractToUse.gamesHashedChoices(gameIDNumber);

      // Extract contract data
      const player1 = game.player1;
      const player2 = game.player2;
      const hashedChoice1 = hashedChoices.hashedChoice1;
      const hashedChoice2 = hashedChoices.hashedChoice2;
      const revealedChoice1 = game.revealedChoice1;
      const revealedChoice2 = game.revealedChoice2;

      // Calculate player-specific data
      const your_address = userAddress;
      const opp_address = (player1.toLowerCase() === userAddress.toLowerCase()) ? player2 : player1;
      const your_hashedChoice = (player1.toLowerCase() === userAddress.toLowerCase()) ? hashedChoice1 : hashedChoice2;
      const your_revealedChoice = (player1.toLowerCase() === userAddress.toLowerCase()) ? revealedChoice1 : revealedChoice2;
      const opp_hashedChoice = (player1.toLowerCase() === userAddress.toLowerCase()) ? hashedChoice2 : hashedChoice1;
      const opp_revealedChoice = (player1.toLowerCase() === userAddress.toLowerCase()) ? revealedChoice2 : revealedChoice1;

      // Calculate flags
      const flag_makeYourChoice = (your_hashedChoice === "0x0000000000000000000000000000000000000000000000000000000000000000");
      const flag_waitOpponentChoice = (!flag_makeYourChoice) && (opp_hashedChoice === "0x0000000000000000000000000000000000000000000000000000000000000000");
      const flag_revealYourChoice = (!flag_makeYourChoice) && (opp_hashedChoice !== "0x0000000000000000000000000000000000000000000000000000000000000000") && (choiceFromUint(your_revealedChoice.toString()) === "Unknown");
      const flag_waitOpponentReveal = (!flag_makeYourChoice) && (opp_hashedChoice !== "0x0000000000000000000000000000000000000000000000000000000000000000") && (choiceFromUint(your_revealedChoice.toString()) !== "Unknown") && (choiceFromUint(opp_revealedChoice.toString()) === "Unknown");
      const flag_gameFinished = (choiceFromUint(revealedChoice1.toString()) !== "Unknown") && (choiceFromUint(revealedChoice2.toString()) !== "Unknown");

      // Calculate status and winner
      let statusString = '';
      let winner = '';
      if (flag_gameFinished) {
        const choice1 = Number(revealedChoice1);
        const choice2 = Number(revealedChoice2);

        if (choiceFromUint(revealedChoice1.toString()) !== "Unknown" && choiceFromUint(revealedChoice2.toString()) !== "Unknown") {
          if (choice1 === choice2) {
            statusString = "It's a tie!";
            winner = '';
          } else if ((choice1 === 1 && choice2 === 3) || (choice1 === 2 && choice2 === 1) || (choice1 === 3 && choice2 === 2)) {
            statusString = `${player1} wins!`;
            winner = player1;
          } else {
            statusString = `${player2} wins!`;
            winner = player2;
          }
        }
      }

      setGameState(prev => ({
        ...prev,
        revealedChoice1: choiceFromUint(revealedChoice1.toString()),
        revealedChoice2: choiceFromUint(revealedChoice2.toString()),
        hashedChoice1,
        hashedChoice2,
        your_hashedChoice,
        your_revealedChoice: choiceFromUint(your_revealedChoice.toString()),
        opp_hashedChoice,
        opp_revealedChoice: choiceFromUint(opp_revealedChoice.toString()),
        flag_makeYourChoice,
        flag_waitOpponentChoice,
        flag_revealYourChoice,
        flag_waitOpponentReveal,
        flag_gameFinished,
        your_address,
        opp_address,
        statusString,
        winner
      }));

      setLoadingStages(prev => ({ ...prev, gameDetails: false }));
      console.log('[RockPaperScissors] Game details loaded');
    } catch (error) {
      console.error('[RockPaperScissors] Failed to load game details:', error);
      setLoadingStages(prev => ({ ...prev, gameDetails: false }));
    }
  }, [gameIDNumber, readOnlyContract, userAddress]);


  const initializeWalletFeatures = useCallback(async () => {
    try {
      if (!userAddress) {
        setLoadingStages(prev => ({ ...prev, userFeatures: false }));
        return;
      }

      // Create signer contract when wallet is available
      const signerContractInstance = await createContract(contractAddresses.RockPaperScissors, RockPaperScissorsConfig.abi, true);
      setSignerContract(signerContractInstance);
      setRockPaperScissorsContract(signerContractInstance);

      setLoadingStages(prev => ({ ...prev, userFeatures: false }));
      console.log('[RockPaperScissors] Wallet features initialized');
    } catch (error) {
      console.error('[RockPaperScissors] Failed to initialize wallet features:', error);
      setLoadingStages(prev => ({ ...prev, userFeatures: false }));
    }
  }, [userAddress]);

  // Main initialization with staggered loading
  useEffect(() => {
    // Show UI immediately
    setUiReady(true);

    // Start loading game basics immediately (non-blocking)
    loadGameBasics();
  }, [loadGameBasics]);

  // Load game details after basics are loaded
  useEffect(() => {
    if (!loadingStages.gameBasics && gameState.player1) {
      loadGameDetails();
    }
  }, [loadingStages.gameBasics, gameState.player1, loadGameDetails]);

  // Initialize wallet features when wallet is ready
  useEffect(() => {
    if (isWalletInitialized && userAddress) {
      initializeWalletFeatures();
    }
  }, [isWalletInitialized, userAddress, initializeWalletFeatures]);


  // Wallet handlers for TopRightBar
  const handleSwitchWallet = () => {
    // For individual game pages, we'll navigate back to main page for wallet switching
    window.location.href = '/';
  };

  const handleWalletDisconnect = async () => {
    try {
      const accounts = await getAccounts();
      setUserAddress(accounts[0] || '');
    } catch (error) {
      console.error('Error after wallet disconnect:', error);
      window.location.href = '/';
    }
  };

  // Event handler for all game events
  const handleGameEvent = useCallback(async (eventName) => {
    console.log(`[RockPaperScissors] ${eventName} event received`);
    await queryGameInstanceInfo();
    
    // Clear transaction pending state when relevant events are received
    const currentPending = transactionPendingRef.current;
    if (currentPending && (
      (eventName === 'ChoiceMade' && currentPending.type === 'commit') ||
      (eventName === 'ChoiceRevealed' && currentPending.type === 'reveal')
    )) {
      console.log(`[RockPaperScissors] Clearing transaction pending for ${eventName}`);
      setTransactionPendingState(null);
    }
  }, [queryGameInstanceInfo]);

  // Polling subscription management
  useEffect(() => {
    if (!loadingStages.gameBasics && gameIDNumber && ethersContract) {
      // Subscribe to blockchain updates using BlockchainDataManager
      console.log('[RockPaperScissors] Setting up blockchain update subscription');

      const handleGameInstanceUpdate = (data) => {
        // Filter updates for this specific game
        if (data.source === 'polling' && (data.contract === 'RockPaperScissors' || data.contract === 'GameMaker')) {
          console.log(`[RockPaperScissors] Polling update from ${data.contract}, refreshing game state...`);
          handleGameEvent('PollingUpdate');
        }
      };

      const unsubscribe = blockchainDataManager.subscribe('gameInstancesUpdated', handleGameInstanceUpdate);

      return unsubscribe;
    }
  }, [loadingStages.gameBasics, gameIDNumber, ethersContract, handleGameEvent]);

  // Setup polling system when component loads
  useEffect(() => {
    if (!loadingStages.gameBasics && ethersContract) {
      const setupPolling = async () => {
        try {
          await blockchainDataManager.setupSmartPolling();
          blockchainDataManager.addToPolledContracts('RockPaperScissors');
          console.log('[RockPaperScissors] Polling system fully initialized with 2 contracts');
        } catch (error) {
          console.error('[RockPaperScissors] Failed to setup polling system:', error);
        }
      };
      setupPolling();
    }
  }, [loadingStages.gameBasics, ethersContract]);

  const choiceToUint = (choice) => {
    switch (choice) {
      case 'Rock':
        return 1;
      case 'Paper':
        return 2;
      case 'Scissors':
        return 3;
      default:
        throw new Error('Invalid choice');
    }
  };

  const choiceFromUint = (choiceUint) => {
    switch (choiceUint) {
      case '1':
        return 'Rock';
      case '2':
        return 'Paper';
      case '3':
        return 'Scissors';
      default:
        return 'Unknown';
    }
  };

  useEffect(() => {
    if (!salt) {
      const generatedSalt = Math.random().toString(36).substring(2);
      setSalt(generatedSalt);
      localStorage.setItem(`salt_RockPaperScissors_${gameID}`, generatedSalt);
    }
  }, [gameID, salt]);

  // Set default reveal choice to original choice when reveal phase starts
  useEffect(() => {
    if (gameState.flag_revealYourChoice && originalChoice && !selectedRevealChoice) {
      setSelectedRevealChoice(originalChoice);
    }
  }, [gameState.flag_revealYourChoice, originalChoice, selectedRevealChoice]);

  // Restore selected choice if user has already made their choice
  useEffect(() => {
    if (gameState.flag_waitOpponentChoice && originalChoice && !selectedChoice) {
      setSelectedChoice(originalChoice);
    }
  }, [gameState.flag_waitOpponentChoice, originalChoice, selectedChoice]);

  // Manage selection states based on game phase
  useEffect(() => {
    // Don't clear the choice selection - preserve it to show what the user selected
    // Only clear reveal choice selection when we're no longer in reveal phase
    if (!gameState.flag_revealYourChoice && selectedRevealChoice) {
      setSelectedRevealChoice(null);
    }
  }, [gameState.flag_revealYourChoice, selectedRevealChoice]);

  // Clear transaction pending state when game progresses (for polling system)
  useEffect(() => {
    const currentPending = transactionPendingRef.current;
    if (currentPending) {
      // Clear commit pending when we're no longer in "make choice" phase
      if (currentPending.type === 'commit' && !gameState.flag_makeYourChoice) {
        console.log(`[RockPaperScissors] Auto-clearing commit transaction pending - game progressed`);
        setTransactionPendingState(null);
      }
      // Clear reveal pending when we're no longer in "reveal" phase
      else if (currentPending.type === 'reveal' && !gameState.flag_revealYourChoice) {
        console.log(`[RockPaperScissors] Auto-clearing reveal transaction pending - game progressed`);
        setTransactionPendingState(null);
      }
    }
  }, [gameState.flag_makeYourChoice, gameState.flag_revealYourChoice]);

  // Auto-pause polling when game finishes
  useEffect(() => {
    const isGameFinished = gameState.flag_gameFinished === true;

    // Dispatch auto-pause event based on game state
    window.dispatchEvent(new CustomEvent('setPollingPause', {
      detail: {
        isPaused: isGameFinished,
        reason: isGameFinished ? 'game-finished' : ''
      }
    }));
  }, [gameState.flag_gameFinished]);

  const makeChoice = async (choice) => {
    console.log('🎯 makeChoice function called with choice:', choice);
    
    if (!userAddress) {
      setErrorMessage('User address not available. Have you connected your wallet?');
      return;
    }

    // Set transaction pending state
    setTransactionPendingState({ 
      type: 'commit',
      choice: choice,
      message: `Committing ${choice} choice...`
    });

    console.log('🔍 makeChoice Debug Info:', {
      userAddress,
      gameID,
      gameIDNumber,
      choice,
      gameState: {
        player1: gameState.player1,
        player2: gameState.player2,
        player1Lower: gameState.player1.toLowerCase(),
        player2Lower: gameState.player2.toLowerCase(),
        userAddressLower: userAddress.toLowerCase(),
        isPlayer1: userAddress.toLowerCase() === gameState.player1.toLowerCase(),
        isPlayer2: userAddress.toLowerCase() === gameState.player2.toLowerCase()
      }
    });

    try {
      const choiceUint = choiceToUint(choice);
      const hashedChoice = ethers.utils.solidityKeccak256(["uint8", "string"], [choiceUint, salt]);
      
      console.log('🎲 About to call contract.makeChoice with:', {
        gameID: gameIDNumber,
        gameIDOriginal: gameID,
        gameIDType: typeof gameIDNumber,
        hashedChoice,
        choiceUint,
        salt,
        contractAddress: rockPaperScissorsContract.address
      });
      
      // Let's also check what the contract thinks about this game
      try {
        const gameInstance = await rockPaperScissorsContract.getGameInstance(gameIDNumber);
        console.log('🔍 Contract game instance check:', {
          gameID: gameIDNumber,
          gameInstance: {
            player1: gameInstance.player1,
            player2: gameInstance.player2,
            gameActive: gameInstance.gameActive
          }
        });
      } catch (gameCheckError) {
        console.error('❌ Error checking game instance:', gameCheckError);
      }
      
      const estimatedGas = await rockPaperScissorsContract.estimateGas.makeChoice(gameIDNumber, hashedChoice);
      const gasLimit = Math.floor(Number(estimatedGas) * 1.2);
      const gasPrice = await provider.getGasPrice();
      const gasPriceBid = Math.floor(Number(gasPrice) * 1.2);

      const tx = await rockPaperScissorsContract.makeChoice(gameIDNumber, hashedChoice, {
        gasLimit: gasLimit,
        gasPrice: gasPriceBid
      });
      await tx.wait(); // Wait for confirmation

      // Store the original choice for later reveal
      setOriginalChoice(choice);
      localStorage.setItem(`originalChoice_RockPaperScissors_${gameID}`, choice);
      
      // Keep the selected choice visible after making it
      // setSelectedChoice(choice); // This is already set from handleBoxClick
      
      setErrorMessage('');
    } catch (error) {
      console.error('Error in makeChoice:', error);
      setErrorMessage(error.message || 'An unexpected error occurred');
      // Clear pending state on error since no blockchain event will be emitted
      setTransactionPendingState(null);
    } finally {
      // Transaction pending state will be cleared by blockchain event handler
    }
  };

  const revealChoice = async (choice) => {
    if (!userAddress) {
      setErrorMessage('User address not available. Have you connected your wallet?');
      return;
    }

    // Set transaction pending state
    setTransactionPendingState({ 
      type: 'reveal',
      choice: choice,
      message: `Revealing ${choice} choice...`
    });

    try {
      const choiceUint = choiceToUint(choice);
      const estimatedGas = await rockPaperScissorsContract.estimateGas.revealChoice(gameIDNumber, choiceUint, salt);
      const gasLimit = Math.floor(Number(estimatedGas) * 1.2);
      const gasPrice = await provider.getGasPrice();
      const gasPriceBid = Math.floor(Number(gasPrice) * 1.2);

      const tx = await rockPaperScissorsContract.revealChoice(gameIDNumber, choiceUint, salt, {
        gasLimit: gasLimit,
        gasPrice: gasPriceBid
      });
      await tx.wait(); // Wait for confirmation

      setErrorMessage('');
    } catch (error) {
      console.error('Error in revealChoice:', error);
      setErrorMessage(error.message || 'An unexpected error occurred');
      // Clear pending state on error since no blockchain event will be emitted
      setTransactionPendingState(null);
    } finally {
      // Transaction pending state will be cleared by blockchain event handler
    }
  };

  const handleBoxClick = (choice) => {
    // Don't allow selection during pending transactions
    if (transactionPending) return;
    setSelectedChoice(choice);
  };

  const formatEth = (wei) => {
    return ethersUtils.fromWei(wei.toString(), 'ether');
  };

  const getUserRole = () => {
    const isSamePlayer = gameState.player1.toLowerCase() === gameState.player2.toLowerCase();
    const isPlayer1 = userAddress && userAddress.toLowerCase() === gameState.player1.toLowerCase();
    const isPlayer2 = userAddress && userAddress.toLowerCase() === gameState.player2.toLowerCase();
    
    if (isSamePlayer && (isPlayer1 || isPlayer2)) {
      return 'Both (Rock, Paper, Scissors)';
    } else if (isPlayer1) {
      return 'Player 1';
    } else if (isPlayer2) {
      return 'Player 2';
    }
    return 'Spectator';
  };

  const getStatusMessage = () => {
    // Add debug info to help identify the issue
    console.log('Game state flags:', {
      flag_makeYourChoice: gameState.flag_makeYourChoice,
      flag_waitOpponentChoice: gameState.flag_waitOpponentChoice,
      flag_revealYourChoice: gameState.flag_revealYourChoice,
      flag_waitOpponentReveal: gameState.flag_waitOpponentReveal,
      flag_gameFinished: gameState.flag_gameFinished,
      statusString: gameState.statusString
    });

    if (gameState.flag_makeYourChoice) {
      return { main: 'Your turn', sub: 'Choose Rock, Paper, or Scissors' };
    } else if (gameState.flag_waitOpponentChoice) {
      return { main: 'Choice made', sub: 'Waiting for opponent to choose...' };
    } else if (gameState.flag_revealYourChoice) {
      return { main: 'Reveal time', sub: 'Click on your original choice to reveal' };
    } else if (gameState.flag_waitOpponentReveal) {
      return { main: 'Waiting for opponent', sub: 'Opponent needs to reveal their choice' };
    } else if (gameState.flag_gameFinished) {
      return { main: 'Game finished', sub: gameState.statusString || '' };
    }
    
    // Fallback based on game state
    if (!gameState.player1 || !gameState.player2) {
      return { main: 'Loading...', sub: 'Getting game data...' };
    }
    
    return { main: 'Initializing...', sub: 'Setting up game...' };
  };

  const getWinnerMessage = () => {
    if (!gameState.flag_gameFinished || !gameState.winner) return '';
    
    const isSamePlayer = gameState.player1.toLowerCase() === gameState.player2.toLowerCase();
    const isPlayer1 = userAddress && userAddress.toLowerCase() === gameState.player1.toLowerCase();
    const isPlayer2 = userAddress && userAddress.toLowerCase() === gameState.player2.toLowerCase();
    const isPlayer = isPlayer1 || isPlayer2;
    
    // Don't show winner message if choices are still unknown
    if (gameState.revealedChoice1 === "Unknown" || gameState.revealedChoice2 === "Unknown") {
      return '';
    }
    
    // For players, show simple win/loss message
    if (isPlayer && !isSamePlayer) {
      if (gameState.winner === 'draw') {
        return 'Draw';
      } else if (gameState.winner === gameState.player1) {
        return isPlayer1 ? 'You Won' : 'You Lost';
      } else if (gameState.winner === gameState.player2) {
        return isPlayer2 ? 'You Won' : 'You Lost';
      }
    }
    
    // For spectators or same player, show detailed message
    if (gameState.winner === 'draw') {
      return 'Game ended in a draw';
    } else if (gameState.winner === gameState.player1) {
      if (isSamePlayer) {
        return `You win with ${gameState.revealedChoice1}!`;
      }
      return `Player 1 wins with ${gameState.revealedChoice1}!`;
    } else if (gameState.winner === gameState.player2) {
      if (isSamePlayer) {
        return `You win with ${gameState.revealedChoice2}!`;
      }
      return `Player 2 wins with ${gameState.revealedChoice2}!`;
    }
    return '';
  };

  const getChoiceIcon = (choice) => {
    switch (choice) {
      case 'Rock':
        return RockIcon;
      case 'Paper':
        return PaperIcon;
      case 'Scissors':
        return ScissorsIcon;
      default:
        return null;
    }
  };

  // Show full skeleton while UI is not ready
  if (!uiReady) {
    return <FullGameSkeleton gameType="rockpaperscissors" />;
  }

  // Show loading while wallet is initializing
  if (!isWalletInitialized) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h1 className="text-4xl font-bold text-gray-600 mb-4">Initializing...</h1>
        <p className="text-gray-500">Connecting to wallet...</p>
      </div>
    );
  }

  // Show loading while user address is being loaded
  if (!userAddress) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h1 className="text-4xl font-bold text-gray-600 mb-4">Loading...</h1>
        <p className="text-gray-500">Getting wallet address...</p>
      </div>
    );
  }

  if (errorMessage && !gameState.player1) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          Error: {errorMessage}
        </div>
      </div>
    );
  }

  console.log('🎯 RockPaperScissorsGame about to render JSX');
  return (
    <div className="container mx-auto px-4 py-8">
      <style>
        {`
          .collapsible-button {
            background: white !important;
            background-color: white !important;
            background-image: none !important;
            color: #374151 !important;
            border: none !important;
            padding: 0 !important;
            border-radius: 0 !important;
          }
          .collapsible-container {
            background-color: white !important;
            border-radius: 0.5rem !important;
            padding: 1.5rem !important;
            margin-bottom: 1.5rem !important;
            border: none !important;
            box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
          }
          .choice-button {
            transition: all 0.2s ease-in-out;
          }
          .choice-button:hover {
            transform: scale(1.05);
          }
          .choice-button.selected {
            ring: 2px solid #3b82f6;
          }
          
          /* Shared game message styling */
          .game-message-main {
            font-size: 1.5rem;
            font-weight: 700;
            color: #10b981;
            text-align: center;
            margin-bottom: 0.5rem;
          }
          .game-message-sub {
            font-size: 1rem;
            font-weight: 400;
            color: #374151;
            text-align: center;
          }
          .game-message-pending {
            font-size: 1rem;
            font-weight: 500;
            color: #f59e0b;
            text-align: center;
          }
          
          /* Win/Loss Animations */
          @keyframes celebrate {
            0%, 100% { 
              transform: scale(1);
              box-shadow: 0 0 10px rgba(16, 185, 129, 0.3);
            }
            50% { 
              transform: scale(1.02);
              box-shadow: 0 0 15px rgba(16, 185, 129, 0.5);
            }
          }
          
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            25% { transform: translateX(-2px); }
            50% { transform: translateX(2px); }
            75% { transform: translateX(-1px); }
          }
          
          .win-animation {
            animation: celebrate 1.5s ease-in-out 3;
            border: 3px solid #10b981 !important;
            box-shadow: 0 0 15px rgba(16, 185, 129, 0.4);
          }
          
          .loss-animation {
            animation: shake 0.5s ease-in-out 3;
          }
          
          .winner-highlight {
            border: 3px solid #10b981 !important;
            background: linear-gradient(to right, rgba(16, 185, 129, 0.1), rgba(16, 185, 129, 0.05)) !important;
            box-shadow: 0 0 15px rgba(16, 185, 129, 0.3) !important;
          }
          
          .player1-winner {
            border: 3px solid #3b82f6 !important;
            background: linear-gradient(to right, rgba(59, 130, 246, 0.1), rgba(59, 130, 246, 0.05)) !important;
            box-shadow: 0 0 15px rgba(59, 130, 246, 0.3) !important;
          }
          
          .player2-winner {
            border: 3px solid #ef4444 !important;
            background: linear-gradient(to right, rgba(239, 68, 68, 0.1), rgba(239, 68, 68, 0.05)) !important;
            box-shadow: 0 0 15px rgba(239, 68, 68, 0.3) !important;
          }
        `}
      </style>
      <div className="max-w-6xl mx-auto">
        {/* Top Navigation Bar */}
        <div className="flex justify-between items-center mb-6">
          <Link
            to="/1on1"
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm transition"
          >
            ← Back to Games
          </Link>

          <TopRightBar
            onSwitchWallet={handleSwitchWallet}
            onWalletDisconnect={handleWalletDisconnect}
            showInTestMode={true}
          />
        </div>

        <h1 className="text-4xl font-bold text-center mb-8 text-gray-800">
          Rock Paper Scissors Game #{gameID}
        </h1>

        {/* Game Rules - Collapsible */}
        <div className="collapsible-container">
          <button 
            className="collapsible-button"
            onClick={() => setRulesCollapsed(!rulesCollapsed)}
            style={{ 
              width: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              fontSize: '1.25rem', 
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            <span>How Rock Paper Scissors Works</span>
            <svg className={`w-5 h-5 transition-transform duration-200 ${rulesCollapsed ? '' : 'rotate-180'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
            rulesCollapsed ? 'max-h-0 opacity-0' : 'max-h-96 opacity-100 mt-3'
          }`}>
            <ul className="list-disc list-inside space-y-2 text-gray-600">
              <li>Both players choose <strong>Rock</strong>, <strong>Paper</strong>, or <strong>Scissors</strong> simultaneously</li>
              <li>Choices are <strong>hidden</strong> using cryptographic hashing until both players have chosen</li>
              <li>Once both have chosen, players <strong>reveal</strong> their choices using their salt</li>
              <li><strong>Rock</strong> beats Scissors, <strong>Scissors</strong> beats Paper, <strong>Paper</strong> beats Rock</li>
              <li>The winner takes all the money from both bets</li>
              <li><strong>Important:</strong> Remember your salt! You need it to reveal your choice and win</li>
              <li>🔍 Find the contract info and verified code here: <AddressDisplayLong address={contractAddresses.RockPaperScissors} useExplorerLink={true} /></li>
            </ul>
          </div>
        </div>

        {/* Game Status */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-700">Game Status</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <p className="text-lg font-medium">{getStatusMessage().main}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Your Role</p>
              <p className="text-lg font-medium">{getUserRole()}</p>
            </div>
          </div>
        </div>

        {/* Game Board */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-700 text-center">Game Board</h2>
          
          {/* Current Choices Display */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            {/* Player 1 Choice */}
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-4 text-gray-700">Player 1 Choice</h3>
              <div className="bg-gray-50 rounded-lg p-6 min-h-32 flex flex-col items-center justify-center">
                {gameState.revealedChoice1 !== "Unknown" ? (
                  <div className="flex flex-col items-center">
                    <img src={getChoiceIcon(gameState.revealedChoice1)} alt={gameState.revealedChoice1} className="w-16 h-16 mb-2" />
                    <span className="text-xl font-bold text-gray-700">{gameState.revealedChoice1}</span>
                  </div>
                ) : gameState.hashedChoice1 !== "0x0000000000000000000000000000000000000000000000000000000000000000" ? (
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gray-300 rounded-lg mb-2 flex items-center justify-center">
                      <span className="text-2xl">🤔</span>
                    </div>
                    <span className="text-sm text-gray-500">Choice made, waiting for reveal</span>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gray-200 rounded-lg mb-2 flex items-center justify-center">
                      <span className="text-2xl">⏳</span>
                    </div>
                    <span className="text-sm text-gray-500">Waiting for choice</span>
                  </div>
                )}
              </div>
            </div>

            {/* Player 2 Choice */}
            <div className="text-center">
              <h3 className="text-lg font-semibold mb-4 text-gray-700">Player 2 Choice</h3>
              <div className="bg-gray-50 rounded-lg p-6 min-h-32 flex flex-col items-center justify-center">
                {gameState.revealedChoice2 !== "Unknown" ? (
                  <div className="flex flex-col items-center">
                    <img src={getChoiceIcon(gameState.revealedChoice2)} alt={gameState.revealedChoice2} className="w-16 h-16 mb-2" />
                    <span className="text-xl font-bold text-gray-700">{gameState.revealedChoice2}</span>
                  </div>
                ) : gameState.hashedChoice2 !== "0x0000000000000000000000000000000000000000000000000000000000000000" ? (
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gray-300 rounded-lg mb-2 flex items-center justify-center">
                      <span className="text-2xl">🤔</span>
                    </div>
                    <span className="text-sm text-gray-500">Choice made, waiting for reveal</span>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gray-200 rounded-lg mb-2 flex items-center justify-center">
                      <span className="text-2xl">⏳</span>
                    </div>
                    <span className="text-sm text-gray-500">Waiting for choice</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Player Action Area - Show for making choices or waiting after choice is made */}
          {(gameState.flag_makeYourChoice || gameState.flag_waitOpponentChoice) && getUserRole() !== 'Spectator' && (
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold mb-4 text-center text-gray-700">
                {gameState.flag_makeYourChoice ? 'Make Your Choice' : 'Your Choice Made'}
              </h3>
              <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
                {['Rock', 'Paper', 'Scissors'].map((choice) => {
                  const isSelected = selectedChoice === choice;
                  const canInteract = gameState.flag_makeYourChoice && !transactionPending;
                  return (
                    <button
                      key={choice}
                      onClick={canInteract ? () => handleBoxClick(choice) : undefined}
                      disabled={!canInteract}
                      className={`choice-button p-4 rounded-lg border-2 transition-all ${
                        isSelected 
                          ? 'border-blue-500 bg-blue-200 ring-2 ring-blue-300' 
                          : canInteract 
                            ? 'border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-100'
                            : 'border-gray-200 bg-gray-50'
                      } ${!canInteract ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      <div className="flex flex-col items-center">
                        <img 
                          src={getChoiceIcon(choice)} 
                          alt={choice} 
                          className={`w-12 h-12 mb-2 ${!canInteract && !isSelected ? 'opacity-50' : ''}`} 
                        />
                        <span className={`text-sm font-medium ${
                          isSelected 
                            ? 'text-blue-800' 
                            : canInteract 
                              ? 'text-gray-700 hover:text-blue-700'
                              : 'text-gray-400'
                        }`}>{choice}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
              
              {selectedChoice && gameState.flag_makeYourChoice && (
                <div className="text-center mt-4">
                  <button 
                    onClick={() => makeChoice(selectedChoice)}
                    disabled={transactionPending}
                    className={`px-6 py-2 rounded-lg transition ${
                      transactionPending 
                        ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                        : 'bg-blue-500 text-white hover:bg-blue-600'
                    }`}
                  >
                    {transactionPending && transactionPending.type === 'commit'
                      ? 'Processing...'
                      : `Make Choice: ${selectedChoice}`
                    }
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Reveal Choice Section - Show when player needs to reveal */}
          {gameState.flag_revealYourChoice && getUserRole() !== 'Spectator' && (
            <div className="border-t pt-6">
              <h3 className="text-lg font-semibold mb-4 text-center text-gray-700">Reveal Your Choice</h3>
              <div className="text-center">
                <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-4">
                  {['Rock', 'Paper', 'Scissors'].map((choice) => (
                    <button
                      key={choice}
                      onClick={() => !transactionPending && setSelectedRevealChoice(choice)}
                      disabled={transactionPending}
                      className={`choice-button p-4 rounded-lg border-2 transition-all ${
                        selectedRevealChoice === choice 
                          ? 'border-blue-500 bg-blue-200 ring-2 ring-blue-300' 
                          : transactionPending 
                            ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                            : 'border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-100 cursor-pointer'
                      }`}
                    >
                      <div className="flex flex-col items-center">
                        <img src={getChoiceIcon(choice)} alt={choice} className="w-12 h-12 mb-2" />
                        <span className={`text-sm font-medium ${
                          selectedRevealChoice === choice 
                            ? 'text-blue-800' 
                            : 'text-gray-700 hover:text-blue-700'
                        }`}>{choice}</span>
                      </div>
                    </button>
                  ))}
                </div>
                
                {selectedRevealChoice && (
                  <div className="text-center mt-4">
                    <button 
                      onClick={() => revealChoice(selectedRevealChoice)}
                      disabled={transactionPending}
                      className={`px-6 py-2 rounded-lg transition ${
                        transactionPending 
                          ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                          : 'bg-green-500 text-white hover:bg-green-600'
                      }`}
                    >
                      {transactionPending && transactionPending.type === 'reveal'
                        ? 'Processing...'
                        : `Reveal Choice: ${selectedRevealChoice}`
                      }
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Salt Input - Only show for players, not spectators, and not when game is finished */}
          {getUserRole() !== 'Spectator' && !gameState.flag_gameFinished && (
            <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
              <h4 className="font-semibold text-gray-700 mb-2">Your Salt (Keep this safe!)</h4>
              <input
                type="text"
                value={salt}
                disabled={transactionPending}
                onChange={(e) => {
                  if (!transactionPending) {
                    setSalt(e.target.value);
                    localStorage.setItem(`salt_RockPaperScissors_${gameID}`, e.target.value);
                  }
                }}
                placeholder="Enter your salt"
                className={`w-full p-2 border rounded-md text-sm font-mono ${
                  transactionPending 
                    ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed' 
                    : 'border-gray-300 bg-white'
                }`}
              />
              <p className="text-xs text-yellow-700 mt-1">Remember your salt! You need it to reveal your choice and win the game.</p>
            </div>
          )}
          
          {/* Game Messages - Below the game board */}
          <div className="text-center mt-6">
            {transactionPending ? (
              <div className="game-message-pending mb-2">
                {transactionPending.message}
              </div>
            ) : !gameState.flag_gameFinished ? (
              <div>
                <div className="game-message-main">{getStatusMessage().main}</div>
                <div className="game-message-sub">{getStatusMessage().sub}</div>
              </div>
            ) : (
              getWinnerMessage() && (
                <div className={`text-center p-4 rounded-lg ${
                  getWinnerMessage() === 'You Won' ? 'win-animation bg-green-50' :
                  getWinnerMessage() === 'You Lost' ? 'loss-animation bg-red-50' :
                  getWinnerMessage() === 'Draw' ? 'bg-yellow-50' : 'bg-gray-50'
                }`}>
                  <p className={`text-2xl font-bold ${
                    getWinnerMessage() === 'You Won' ? 'text-green-600' :
                    getWinnerMessage() === 'You Lost' ? 'text-red-600' :
                    getWinnerMessage() === 'Draw' ? 'text-yellow-600' : 'text-gray-600'
                  }`}>{getWinnerMessage()}</p>
                </div>
              )
            )}
          </div>
        </div>

        {/* Players Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Player 1 */}
          <div className={`bg-white rounded-lg shadow-lg p-6 ${
            gameState.winner === gameState.player1 ? 'player1-winner' : ''
          }`}>
            <h3 className="text-xl font-semibold mb-3 text-gray-700">Player 1</h3>
            <div className="space-y-2">
              <div>
                <p className="text-sm text-gray-600">Address</p>
                <p className="font-mono text-xs break-all"><AddressDisplayLong address={gameState.player1} useExplorerLink={true} /></p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Bet Amount</p>
                <p className="text-lg font-medium">{formatEth(gameState.contribution1)} POL</p>
              </div>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center mr-2">
                  <span className="text-white font-bold">1</span>
                </div>
                <span className="text-gray-700">Player 1 Role</span>
              </div>
              {gameState.winner === gameState.player1 && (
                <div className="text-blue-600 font-semibold">🎉 Winner!</div>
              )}
            </div>
          </div>

          {/* Player 2 */}
          <div className={`bg-white rounded-lg shadow-lg p-6 ${
            gameState.winner === gameState.player2 ? 'player2-winner' : ''
          }`}>
            <h3 className="text-xl font-semibold mb-3 text-gray-700">Player 2</h3>
            <div className="space-y-2">
              <div>
                <p className="text-sm text-gray-600">Address</p>
                <p className="font-mono text-xs break-all"><AddressDisplayLong address={gameState.player2} useExplorerLink={true} /></p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Bet Amount</p>
                <p className="text-lg font-medium">{formatEth(gameState.contribution2)} POL</p>
              </div>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-2">
                  <span className="text-white font-bold">2</span>
                </div>
                <span className="text-gray-700">Player 2 Role</span>
              </div>
              {gameState.winner === gameState.player2 && (
                <div className="text-red-600 font-semibold">🎉 Winner!</div>
              )}
            </div>
          </div>
        </div>

        {/* Game Details */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h3 className="text-xl font-semibold mb-3 text-gray-700">Game Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div>
              <p className="text-sm text-gray-600">Game ID</p>
              <p className="font-mono">{gameID}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Pot</p>
              <p className="text-lg font-medium">
                {formatEth((parseInt(gameState.contribution1) + parseInt(gameState.contribution2)).toString())} POL
              </p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Last Move</p>
              <p>{gameState.lastMoveTime > 0 ? new Date(gameState.lastMoveTime * 1000).toLocaleString() : 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Contract Address</p>
              <div className="font-mono text-xs"><AddressDisplayLong address={contractAddresses.RockPaperScissors} useExplorerLink={true} /></div>
            </div>
          </div>
          
          {/* Transaction Details - Unified Component */}
          <GameTransactionHistory
            gameId={gameID}
            gameMakerContract={gameMakerContract}
            gameContract={readOnlyContract}
            gameEvents={rpsGameEvents}
            player1={gameState.flag_player1}
            player2={gameState.flag_player2}
          />
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mt-4">
            {errorMessage}
          </div>
        )}
      </div>
    </div>
  );
};

export default RockPaperScissorsGame;